import { useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EcnSummaryDto, ListModelsResponse } from '@bhops/shared';
import { ecnsApi } from '@/api/ecns';
import { modelsApi } from '@/api/models';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';
import { canHardDeleteCatalog } from '@/permissions';
import { Button, Field, Input, Select, Textarea, FormError } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Icon } from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';

// S2 Phase 2.3 — ECN(제조 변경점) 마스터 카탈로그 + 역추적(recall) 페이지.
//
// - 키워드 검색(on-submit) → GET /ecns?q=
// - "변경점 추가" 폼 (description + 최초 적용일 + 선택 targeting)
// - 인라인 편집 + 소프트 삭제(보관)
// - ECN 표기는 항상 "날짜 · 설명 (코드)" — code 단독 표기 금지.
// - ECN 클릭 → 역추적 뷰(expand)에서 GET /ecns/:id/robots, 적용 로봇 목록 +
//   "CSV 내보내기"(로드된 rows 의 client-side CSV).
//
// 권한: manager+ 가 mutation 가능(백엔드 가드). 페이지 진입은 제조 그룹에서
// 누구나 가능하되, mutation 실패는 extractApiError 로 표면화.

// ECN 한 줄 표기 — 항상 "YYYY-MM-DD · 설명 (코드)". firstAppliedAt 가 없으면
// 날짜 토큰을 생략하고 "설명 (코드)" 만.
function formatEcnLabel(
  ecn: Pick<EcnSummaryDto, 'code' | 'description' | 'firstAppliedAt'>,
): string {
  // 추가수정 2차(26-07-27) §2 — ISO 를 그대로 자르면 UTC 날짜였다. KST 로 환산한다.
  // 이 한 줄 표기는 로봇 등록/부품 화면에서도 같은 문자열로 쓰이므로 구분자는
  // 문서화된 하이픈(YYYY-MM-DD)을 유지한다(점 표기로 바꾸면 화면마다 어긋난다).
  const date = kstYmd(ecn.firstAppliedAt)?.replace(/\./g, '-') ?? null;
  const head = date ? `${date} · ${ecn.description}` : ecn.description;
  return `${head} (${ecn.code})`;
}

// [신규 자동 적용] 은 앞으로 등록할 로봇에만 걸린다(오너 결정 ①A). 기존 로봇의
// 소급 적용은 로봇 상세의 수동 태깅이 담당하므로 그 경계를 툴팁으로 못박는다.
const AUTO_APPLY_HINT = (
  <>
    <strong>새 로봇 등록 창</strong>에서 미리 체크된 상태로 나옵니다.
    {'\n'}이미 등록된 로봇에는 적용되지 않습니다.
  </>
);

// 모델 목록 — ModelPicker 와 같은 캐시 키를 써서 화면당 1회만 받아온다.
function useModels() {
  return useQuery<ListModelsResponse>({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
}

export function EcnCatalogPage() {
  const queryClient = useQueryClient();
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modelFilter, setModelFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const modelsQ = useModels();

  const listQ = useQuery({
    queryKey: ['ecns', { q: appliedSearch, includeInactive, modelId: modelFilter }],
    queryFn: () =>
      ecnsApi.list({
        q: appliedSearch || undefined,
        active: includeInactive ? 'all' : undefined,
        modelId: modelFilter || undefined,
      }),
    staleTime: 30_000,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['ecns'] });
  }

  function onSubmitSearch(e: FormEvent) {
    e.preventDefault();
    setAppliedSearch(searchDraft.trim());
  }

  const items = listQ.data?.items ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          변경점(ECN) 카탈로그
        </h1>
      </header>

      {/* 검색 + 필터 */}
      <form onSubmit={onSubmitSearch} style={searchBar}>
        <Input
          placeholder="설명 또는 코드 검색 (예: 불감지, N2505)"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          style={{ flex: '1 1 260px' }}
          aria-label="변경점 검색"
        />
        <Button type="submit">검색</Button>
        {appliedSearch && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setSearchDraft('');
              setAppliedSearch('');
            }}
          >
            초기화
          </Button>
        )}
        <Select
          value={modelFilter}
          onChange={(e) => setModelFilter(e.target.value)}
          aria-label="모델 필터"
          style={{ width: 'auto', minWidth: 160 }}
        >
          <option value="">모델 전체</option>
          {(modelsQ.data?.items ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName} ({m.code})
            </option>
          ))}
        </Select>
        <label style={inlineCheck}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          보관 포함
        </label>
      </form>
      {modelFilter && (
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          이 모델 전용 변경점과 함께 <strong>전 모델 공통</strong> 변경점도 같이 나옵니다.
        </div>
      )}

      {/* 신규 등록 */}
      <CreateEcnForm onCreated={invalidate} />

      {/* 리스트 */}
      <section style={card}>
        <div style={listHeader}>
          <strong style={{ fontSize: 13 }}>변경점 ({items.length})</strong>
        </div>
        {listQ.isLoading ? (
          <div style={emptyRow}>불러오는 중…</div>
        ) : listQ.error ? (
          <FormError>{extractApiError(listQ.error)}</FormError>
        ) : items.length === 0 ? (
          <div style={emptyRow}>변경점이 없습니다. 위에서 추가해 주세요.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((ecn) => (
              <EcnRow
                key={ecn.id}
                ecn={ecn}
                expanded={expandedId === ecn.id}
                editing={editingId === ecn.id}
                onToggleExpand={() => setExpandedId((cur) => (cur === ecn.id ? null : ecn.id))}
                onStartEdit={() => setEditingId(ecn.id)}
                onStopEdit={() => setEditingId(null)}
                onChanged={invalidate}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// 신규 등록 폼
// ============================================================

function CreateEcnForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <div>
        <Button type="button" onClick={() => setOpen(true)}>
          + 변경점 추가
        </Button>
      </div>
    );
  }

  return (
    <div style={addCard}>
      <strong style={{ fontSize: 13 }}>변경점 추가</strong>
      <div style={{ marginTop: 8 }}>
        <EcnCreateFields
          onCreated={() => {
            onCreated();
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      </div>
    </div>
  );
}

/**
 * 변경점 추가 칸과 [저장] · [취소]. 이 화면의 추가 카드와 로봇 상세 '부품' 탭의 변경점 보드
 * (EcnApplyBoard, 2026-10-28 오너 "여기에 변경점 추가 버튼도 넣어야해")가 같이 쓴다.
 * initialModelIds — 로봇 상세에서 열면 그 로봇의 모델을 미리 골라 둔다(빈 배열 = 전 모델 공통).
 */
export function EcnCreateFields({
  onCreated,
  onCancel,
  initialModelIds = [],
}: {
  onCreated: (ecn: EcnSummaryDto) => void;
  onCancel: () => void;
  initialModelIds?: string[];
}) {
  const [description, setDescription] = useState('');
  const [firstAppliedAt, setFirstAppliedAt] = useState('');
  const [targetGeneration, setTargetGeneration] = useState('');
  const [targetPcType, setTargetPcType] = useState('');
  const [targetFeature, setTargetFeature] = useState('');
  const [modelIds, setModelIds] = useState<string[]>(initialModelIds);
  const [autoApply, setAutoApply] = useState(false);

  const createM = useMutation({
    mutationFn: () =>
      ecnsApi.create({
        description: description.trim(),
        // date input(YYYY-MM-DD) → UTC 자정 ISO 로 변환(code 의 YYMMDD 산출 일관성).
        firstAppliedAt: firstAppliedAt ? `${firstAppliedAt}T00:00:00.000Z` : undefined,
        targetGeneration: targetGeneration ? Number(targetGeneration) : undefined,
        targetPcType: targetPcType.trim() || undefined,
        targetFeature: targetFeature.trim() || undefined,
        // 빈 배열 = 전 모델 공통. targetModelPrefix(자유 입력)는 폐기됐다.
        modelIds,
        autoApplyOnRegistration: autoApply,
      }),
    onSuccess: (ecn) => onCreated(ecn),
  });

  const canSubmit = description.trim().length > 0 && firstAppliedAt.length > 0;

  return (
    <>
      <div>
        <Field label="설명" htmlFor="ecn-desc" required hint="예: 신규 불감지센서 적용 (SMPS 분리)">
          <Textarea
            id="ecn-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="변경점 내용"
          />
        </Field>
        {/* round5 #23 — 최초 적용일을 타게팅 4필드와 같은 행의 맨 왼쪽에 배치.
            round5 #13 — 세대 필드의 '1 / 2' 힌트는 분수처럼 오독되어 삭제. */}
        <div style={targetGrid}>
          <Field
            label="최초 적용일"
            htmlFor="ecn-date"
            required
            hint="이 날짜로 코드(N{YYMMDD}{NN})가 자동 생성됩니다."
          >
            <Input
              id="ecn-date"
              type="date"
              value={firstAppliedAt}
              onChange={(e) => setFirstAppliedAt(e.target.value)}
            />
          </Field>
          <Field label="세대 (선택)" htmlFor="ecn-gen">
            <Input
              id="ecn-gen"
              type="number"
              min={1}
              value={targetGeneration}
              onChange={(e) => setTargetGeneration(e.target.value)}
            />
          </Field>
          <Field label="PC 타입 (선택)" htmlFor="ecn-pc">
            <Input
              id="ecn-pc"
              value={targetPcType}
              onChange={(e) => setTargetPcType(e.target.value)}
              placeholder="U300"
            />
          </Field>
          <Field label="특성 (선택)" htmlFor="ecn-feature">
            <Input
              id="ecn-feature"
              value={targetFeature}
              onChange={(e) => setTargetFeature(e.target.value)}
              placeholder="불감지"
            />
          </Field>
        </div>
        <ModelMultiSelect idPrefix="ecn-new" value={modelIds} onChange={setModelIds} />
        <AutoApplyToggle
          id="ecn-new-auto-apply"
          checked={autoApply}
          onChange={setAutoApply}
          testId="ecn-create-auto-apply"
        />
      </div>

      {createM.isError && <FormError>{extractApiError(createM.error)}</FormError>}

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <Button
          type="button"
          onClick={() => createM.mutate()}
          disabled={!canSubmit || createM.isPending}
        >
          {createM.isPending ? '저장 중…' : '저장'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          취소
        </Button>
      </div>
    </>
  );
}

// ============================================================
// 적용 모델 다중 선택 — 아무것도 안 고르면 "전 모델 공통"
// ============================================================

function ModelMultiSelect({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const modelsQ = useModels();
  const models = modelsQ.data?.items ?? [];

  function toggle(id: string): void {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div className="kuun-label" style={{ marginBottom: 4 }}>
        적용 모델
      </div>
      {modelsQ.isLoading ? (
        <div style={emptyRow}>모델 불러오는 중…</div>
      ) : models.length === 0 ? (
        <div style={emptyRow}>등록된 모델이 없습니다.</div>
      ) : (
        <div style={modelPickGrid} data-testid={`${idPrefix}-models`}>
          {models.map((m) => (
            <label key={m.id} style={modelPickItem}>
              <input
                type="checkbox"
                checked={value.includes(m.id)}
                onChange={() => toggle(m.id)}
                data-testid={`${idPrefix}-model-${m.id}`}
              />
              {m.displayName} ({m.code})
            </label>
          ))}
        </div>
      )}
      <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
        {value.length === 0
          ? '아무것도 고르지 않으면 전 모델 공통으로 저장됩니다.'
          : `${value.length}개 모델에만 적용됩니다.`}
      </div>
    </div>
  );
}

// [신규 자동 적용] 스위치 — 폼과 목록 행에서 같은 문구/툴팁을 쓴다.
function AutoApplyToggle({
  id,
  checked,
  onChange,
  disabled,
  testId,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <Tooltip content={AUTO_APPLY_HINT} focusable={false}>
      <label htmlFor={id} style={autoApplyLabel}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
        />
        신규 자동 적용
      </label>
    </Tooltip>
  );
}

// 적용 모델 배지 — 빈 배열이면 '전 모델'.
function ModelBadges({ ecn }: { ecn: EcnSummaryDto }) {
  if (ecn.models.length === 0) {
    return <span style={badgeAll}>전 모델</span>;
  }
  return (
    <>
      {ecn.models.map((m) => (
        <span key={m.id} style={badgeModel}>
          {m.displayName}
        </span>
      ))}
    </>
  );
}

// ============================================================
// ECN 한 행 (표기 + 액션 + 인라인 편집 + 역추적 expand)
// ============================================================

interface EcnRowProps {
  ecn: EcnSummaryDto;
  expanded: boolean;
  editing: boolean;
  onToggleExpand: () => void;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onChanged: () => void;
}

function EcnRow({
  ecn,
  expanded,
  editing,
  onToggleExpand,
  onStartEdit,
  onStopEdit,
  onChanged,
}: EcnRowProps) {
  const role = useAuthStore((s) => s.user?.role);
  const canHardDelete = role ? canHardDeleteCatalog(role) : false;
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);

  const removeM = useMutation({
    mutationFn: () => ecnsApi.remove(ecn.id),
    onSuccess: onChanged,
  });

  const restoreM = useMutation({
    mutationFn: () => ecnsApi.update(ecn.id, { active: true }),
    onSuccess: onChanged,
  });

  const hardDeleteM = useMutation({
    mutationFn: () => ecnsApi.hardDelete(ecn.id),
    onSuccess: () => {
      setConfirmHardDelete(false);
      onChanged();
    },
  });

  const autoApplyM = useMutation({
    mutationFn: (next: boolean) => ecnsApi.update(ecn.id, { autoApplyOnRegistration: next }),
    onSuccess: onChanged,
  });

  // 적용 이력이 남아 있으면 서버가 409 로 막는다 — 눌러보고 알게 하지 말고
  // 버튼 자체를 비활성 + 사유를 title 로.
  const blockedReason =
    ecn.appliedRobotCount > 0
      ? `적용 이력이 ${ecn.appliedRobotCount}건 있어 완전삭제할 수 없습니다. 먼저 적용을 해제해 주세요.`
      : null;

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <div style={rowMain}>
        <button type="button" onClick={onToggleExpand} style={rowLabelBtn} aria-expanded={expanded}>
          <span style={{ marginRight: 8, color: 'var(--fg-muted)', display: 'inline-flex' }}>
            {expanded ? (
              <Icon name="chev-down" size={14} aria-hidden />
            ) : (
              <Icon name="chev-right" size={14} aria-hidden />
            )}
          </span>
          <span style={{ fontWeight: 500, opacity: ecn.active ? 1 : 0.5 }}>
            {formatEcnLabel(ecn)}
          </span>
          {!ecn.active && <span style={badgeMuted}>보관됨</span>}
          <ModelBadges ecn={ecn} />
          {ecn.autoApplyOnRegistration && <span style={badgeAuto}>신규 자동 적용</span>}
        </button>
        <Tooltip
          content={
            <>
              <strong>{ecn.code}</strong>
              {'\n'}
              {ecn.description}
              {'\n'}적용 {ecn.appliedActiveRobotCount}대
            </>
          }
        >
          {/* 표시는 삭제된 로봇을 뺀 수. 완전삭제 차단은 이력 줄 수(appliedRobotCount) — 다른 물음이다. */}
          <span style={countChip}>적용 {ecn.appliedActiveRobotCount}대</span>
        </Tooltip>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <AutoApplyToggle
            id={`ecn-auto-${ecn.id}`}
            checked={ecn.autoApplyOnRegistration}
            onChange={(next) => autoApplyM.mutate(next)}
            disabled={autoApplyM.isPending}
            testId={`ecn-auto-apply-${ecn.id}`}
          />
          <Button type="button" variant="secondary" onClick={onStartEdit}>
            편집
          </Button>
          {ecn.active ? (
            <Button
              type="button"
              variant="danger"
              onClick={() => removeM.mutate()}
              disabled={removeM.isPending}
            >
              {removeM.isPending ? '…' : '보관'}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => restoreM.mutate()}
                disabled={restoreM.isPending}
                data-testid={`ecn-restore-${ecn.id}`}
              >
                {restoreM.isPending ? '…' : '복원'}
              </Button>
              {canHardDelete && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setConfirmHardDelete(true)}
                  disabled={blockedReason !== null || hardDeleteM.isPending}
                  title={blockedReason ?? '되돌릴 수 없습니다.'}
                  data-testid={`ecn-hard-delete-${ecn.id}`}
                >
                  완전삭제
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {removeM.isError && <FormError>{extractApiError(removeM.error)}</FormError>}
      {restoreM.isError && <FormError>{extractApiError(restoreM.error)}</FormError>}
      {hardDeleteM.isError && <FormError>{extractApiError(hardDeleteM.error)}</FormError>}
      {autoApplyM.isError && <FormError>{extractApiError(autoApplyM.error)}</FormError>}

      <ConfirmDialog
        open={confirmHardDelete}
        title="변경점 완전삭제"
        message={`${formatEcnLabel(ecn)}\n\n완전삭제하면 되돌릴 수 없습니다. 계속할까요?`}
        confirmLabel="완전삭제"
        danger
        busy={hardDeleteM.isPending}
        onConfirm={() => hardDeleteM.mutate()}
        onCancel={() => setConfirmHardDelete(false)}
      />

      {editing && (
        <EcnEditForm
          ecn={ecn}
          onSaved={() => {
            onStopEdit();
            onChanged();
          }}
          onCancel={onStopEdit}
        />
      )}

      {expanded && <EcnRecallView ecn={ecn} />}
    </div>
  );
}

// ============================================================
// 인라인 편집 폼
// ============================================================

function EcnEditForm({
  ecn,
  onSaved,
  onCancel,
}: {
  ecn: EcnSummaryDto;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState(ecn.description);
  const [firstAppliedAt, setFirstAppliedAt] = useState(ecn.firstAppliedAt?.slice(0, 10) ?? '');
  const [targetFeature, setTargetFeature] = useState(ecn.targetFeature ?? '');
  const [modelIds, setModelIds] = useState<string[]>(ecn.models.map((m) => m.id));
  const [autoApply, setAutoApply] = useState(ecn.autoApplyOnRegistration);

  const updateM = useMutation({
    mutationFn: () =>
      ecnsApi.update(ecn.id, {
        description: description.trim(),
        firstAppliedAt: firstAppliedAt ? `${firstAppliedAt}T00:00:00.000Z` : undefined,
        targetFeature: targetFeature.trim() || undefined,
        // 전체 교체(set) — 빈 배열이면 전 모델 공통으로 되돌린다.
        modelIds,
        autoApplyOnRegistration: autoApply,
        // 보관됐던 항목을 편집 저장 시 다시 활성화(복원 동선).
        active: true,
      }),
    onSuccess: onSaved,
  });

  return (
    <div style={editCard}>
      <Field label="설명" htmlFor={`edit-desc-${ecn.id}`} required>
        <Textarea
          id={`edit-desc-${ecn.id}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div style={targetGrid}>
        <Field label="최초 적용일" htmlFor={`edit-date-${ecn.id}`}>
          <Input
            id={`edit-date-${ecn.id}`}
            type="date"
            value={firstAppliedAt}
            onChange={(e) => setFirstAppliedAt(e.target.value)}
          />
        </Field>
        <Field label="특성 (선택)" htmlFor={`edit-feature-${ecn.id}`}>
          <Input
            id={`edit-feature-${ecn.id}`}
            value={targetFeature}
            onChange={(e) => setTargetFeature(e.target.value)}
          />
        </Field>
      </div>
      <ModelMultiSelect idPrefix={`edit-${ecn.id}`} value={modelIds} onChange={setModelIds} />
      <div style={{ margin: '8px 0' }}>
        <AutoApplyToggle
          id={`edit-auto-${ecn.id}`}
          checked={autoApply}
          onChange={setAutoApply}
          testId={`ecn-edit-auto-apply-${ecn.id}`}
        />
      </div>
      <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginBottom: 8 }}>
        코드 <span style={{ fontFamily: 'var(--font-mono)' }}>{ecn.code}</span> 는 변경되지
        않습니다.
      </div>
      {updateM.isError && <FormError>{extractApiError(updateM.error)}</FormError>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          type="button"
          onClick={() => updateM.mutate()}
          disabled={!description.trim() || updateM.isPending}
        >
          {updateM.isPending ? '저장 중…' : '저장'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          취소
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// 역추적(recall) 뷰 — 적용 로봇 + CSV 내보내기
// ============================================================

function EcnRecallView({ ecn }: { ecn: EcnSummaryDto }) {
  const robotsQ = useQuery({
    queryKey: ['ecn-robots', ecn.id],
    queryFn: () => ecnsApi.listRobots(ecn.id),
    staleTime: 30_000,
  });

  const robots = robotsQ.data?.items ?? [];

  function exportCsv() {
    const header = ['serialShort', 'serialLong', 'modelCode', 'appliedAt', 'source'];
    const lines = robots.map((r) =>
      [r.serialShort ?? '', r.serialLong ?? '', r.modelCode, r.appliedAt, r.source]
        .map(csvCell)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\r\n');
    // BOM 추가(엑셀에서 한글 깨짐 방지).
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ecn-${ecn.code}-robots.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={recallCard}>
      <div style={recallHeader}>
        <strong style={{ fontSize: 12 }}>이 변경점이 적용된 로봇</strong>
        <Button
          type="button"
          variant="secondary"
          onClick={exportCsv}
          disabled={robots.length === 0}
        >
          CSV 내보내기
        </Button>
      </div>

      {robotsQ.isLoading ? (
        <div style={emptyRow}>불러오는 중…</div>
      ) : robotsQ.error ? (
        <FormError>{extractApiError(robotsQ.error)}</FormError>
      ) : robots.length === 0 ? (
        <div style={emptyRow}>이 변경점이 적용된 로봇이 없습니다.</div>
      ) : (
        <table style={tableBase}>
          <thead>
            <tr>
              <th style={th}>#시리얼</th>
              <th style={th}>모델</th>
              <th style={th}>적용일</th>
              <th style={th}>출처</th>
              <th style={th}>비고</th>
            </tr>
          </thead>
          <tbody>
            {robots.map((r) => (
              <tr key={r.robotId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                  #{r.serialShort ?? r.serialLong ?? r.robotId.slice(0, 8)}
                </td>
                <td style={td}>{r.modelCode || '—'}</td>
                {/* 추가수정 2차(26-07-27) §2 — 역추적 표의 적용일도 KST 로. */}
                <td style={td}>{kstYmd(r.appliedAt) ?? '—'}</td>
                <td style={td}>{r.source}</td>
                <td style={td}>{r.note ?? <span style={{ color: 'var(--fg-muted)' }}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// CSV 셀 escape — 콤마/따옴표/개행 포함 시 따옴표로 감싸고 내부 따옴표는 이중화.
function csvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// ============================================================
// Styles
// ============================================================

const searchBar: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};

const inlineCheck: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 12,
};

const addCard: CSSProperties = {
  ...card,
  background: 'var(--bg-sunken)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const editCard: CSSProperties = {
  padding: '8px 12px 12px',
  background: 'var(--bg-sunken)',
};

const recallCard: CSSProperties = {
  padding: '8px 12px 12px',
  background: 'var(--bg-sunken)',
};

const recallHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 8,
};

const listHeader: CSSProperties = {
  marginBottom: 4,
};

const rowMain: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '8px 4px',
};

const rowLabelBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--fg-default)',
  flex: 1,
  minWidth: 0,
};

const countChip: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-pill)',
  padding: '1px 8px',
};

const badgeMuted: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-pill)',
  padding: '1px 6px',
};

const badgeAll: CSSProperties = {
  ...badgeMuted,
  borderStyle: 'dashed',
};

const badgeModel: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-brand)',
  background: 'var(--brand-subtle)',
  borderRadius: 'var(--r-pill)',
  padding: '1px 8px',
};

const badgeAuto: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-on-success-tint)',
  background: 'var(--bg-success-subtle)',
  borderRadius: 'var(--r-pill)',
  padding: '1px 8px',
};

const autoApplyLabel: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const modelPickGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: 4,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: 8,
  maxHeight: 160,
  overflowY: 'auto',
};

const modelPickItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-default)',
  cursor: 'pointer',
};

const targetGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 8,
};

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = {
  fontSize: 13,
  padding: '6px 10px',
  verticalAlign: 'middle',
};

const emptyRow: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
};
